import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ProfileCodeChip } from "@/components/ProfileCodeChip";
import { BUSINESS_TYPE_LABELS } from "@/lib/constants";
import { Save, MapPin, Plus, Trash2, Pencil, Crown, ArrowRight } from "lucide-react";
import { DepartmentPicker, RolePicker } from "./DepartmentPicker";
import { PermissionMatrix } from "./PermissionMatrix";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PartnerRequests } from "./PartnerRequests";
import { deptName } from "./useOrg";

const Card = ({ children, className = "", ...p }) => <div className={`rounded-2xl border border-[#E5E0D8] bg-white p-5 ${className}`} {...p}>{children}</div>;

export function ProfileTab() {
  const { business: b } = useAuth();
  const navigate = useNavigate();
  const rows = [["Business name", b?.name], ["Business type", BUSINESS_TYPE_LABELS[b?.business_type]], ["Owner (Super Admin)", b?.owner_name],
    ["Mobile", b?.owner_mobile], ["Email", b?.business_email], ["GSTIN", b?.gstin || "—"], ["License", `${b?.license_type || ""} · ${b?.license_number || ""}`], ["Address", b?.address]];
  return (
    <Card data-testid="org-profile-tab">
      <div className="flex flex-wrap items-center justify-between gap-3"><ProfileCodeChip code={b?.profile_code} />
        <Button variant="outline" onClick={() => navigate("/app/profile")} data-testid="org-edit-profile-btn">Edit profile <ArrowRight className="ml-1 h-4 w-4" /></Button></div>
      <dl className="mt-4 grid gap-3 sm:grid-cols-2">
        {rows.map(([k, v]) => <div key={k}><dt className="text-xs uppercase tracking-wider text-stone-400">{k}</dt><dd className="text-sm font-medium text-stone-800">{v || "—"}</dd></div>)}
      </dl>
    </Card>
  );
}

export function DepartmentsTab({ data, reload }) {
  const [deps, setDeps] = useState(data.setup.departments);
  const [roles, setRoles] = useState(data.setup.roles);
  const save = async () => {
    try { await api.put("/org/setup", { departments: deps, roles: Object.fromEntries(Object.entries(roles).filter(([k]) => !deps.length || deps.includes(k))) }); toast.success("Departments saved"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const count = (k) => data.employees.filter((e) => (e.assignments || []).some((a) => a.department === k)).length;
  return (
    <div className="space-y-5" data-testid="departments-tab">
      <div className="flex flex-wrap gap-2">{deps.map((k) => <span key={k} className="rounded-full bg-[#F0ECE1] px-3 py-1 text-xs font-semibold text-[#0D5C3A]" data-testid={`dept-count-${k}`}>{deptName(data, k)} · {count(k)}</span>)}</div>
      <DepartmentPicker catalog={data.catalog} selected={deps} onChange={setDeps} onReload={reload} />
      <RolePicker catalog={data.catalog} departments={deps} roles={roles} onChange={setRoles} onReload={reload} />
      <Button onClick={save} data-testid="save-departments-btn" className="bg-[#0D5C3A] hover:bg-[#084229]"><Save className="mr-1 h-4 w-4" /> Save departments & roles</Button>
    </div>
  );
}

// Flatten the reporting tree (owner at root) into rows with depth; unknown/missing managers fall back to the owner.
function flattenTree(data) {
  const rootId = data.owner?.id || "root";
  const ids = new Set([rootId, ...data.employees.map((e) => e.id)]);
  const parentOf = (e) => (e.reporting_manager_id && ids.has(e.reporting_manager_id) && e.reporting_manager_id !== e.id ? e.reporting_manager_id : rootId);
  const rows = [];
  const seen = new Set();
  const stack = [{ id: rootId, depth: 0, person: data.owner }];
  while (stack.length) {
    const cur = stack.pop();
    if (seen.has(cur.id)) continue;
    seen.add(cur.id);
    if (cur.person) rows.push(cur);
    const kids = data.employees.filter((e) => parentOf(e) === cur.id && !seen.has(e.id));
    for (let i = kids.length - 1; i >= 0; i--) stack.push({ id: kids[i].id, depth: cur.depth + 1, person: kids[i] });
  }
  data.employees.forEach((e) => { if (!seen.has(e.id)) rows.push({ id: e.id, depth: 1, person: e }); });
  return rows;
}

export function StructureTab({ data }) {
  const rows = flattenTree(data);
  return (
    <Card data-testid="structure-tab" className="overflow-x-auto">
      {rows.map(({ id, depth, person }) => (
        <div key={id} style={{ marginLeft: depth * 24 }} className={depth ? "border-l-2 border-[#E5E0D8] pl-4" : ""}>
          <div className="my-1.5 inline-flex items-center gap-2 rounded-xl border border-[#E5E0D8] bg-white px-3 py-2" data-testid={`structure-node-${id}`}>
            {depth === 0 && <Crown className="h-4 w-4 text-[#C85A32]" />}
            <span className="text-sm font-semibold text-stone-800">{person.name}</span>
            <span className="text-xs text-stone-400">{depth === 0 ? "Super Admin" : `${person.job_role} · ${deptName(data, person.department)}`}</span>
          </div>
        </div>
      ))}
    </Card>
  );
}

export function PermissionsTab({ data, reload }) {
  const [sel, setSel] = useState(data.employees[0]?.id || "");
  const emp = data.employees.find((e) => e.id === sel);
  const [perms, setPerms] = useState(emp?.permissions || []);
  useEffect(() => { setPerms(emp?.permissions || []); }, [sel]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    try {
      await api.put(`/org/employees/${emp.id}`, { full_name: emp.name, mobile: emp.mobile, email: emp.email || null, assignments: emp.assignments,
        permissions: perms, reporting_manager_id: emp.reporting_manager_id || null, branch_id: emp.branch_id || null });
      toast.success("Permissions saved"); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  if (!data.employees.length) return <Card data-testid="permissions-tab"><p className="text-sm text-stone-500">Add employees to manage their access here. As Super Admin you always have full access.</p></Card>;
  return (
    <div className="space-y-4" data-testid="permissions-tab">
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-72">
          <Select value={sel} onValueChange={setSel}>
            <SelectTrigger data-testid="permissions-employee-select"><SelectValue /></SelectTrigger>
            <SelectContent>{data.employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} — {e.job_role}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={save} data-testid="permissions-save-btn" className="bg-[#0D5C3A] hover:bg-[#084229]"><Save className="mr-1 h-4 w-4" /> Save permissions</Button>
      </div>
      <PermissionMatrix modules={data.permissions} value={perms} onChange={setPerms} />
      <p className="text-xs text-stone-400">Tip: click an area name to tick or clear its whole row.</p>
    </div>
  );
}

export function BranchesTab({ data, reload }) {
  const { business } = useAuth();
  const [f, setF] = useState({ name: "", address: "" });
  const [edit, setEdit] = useState(null);
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const submit = () => run(async () => {
    if (edit) await api.put(`/org/branches/${edit}`, f); else await api.post("/org/branches", f);
    setF({ name: "", address: "" }); setEdit(null);
  }, edit ? "Branch updated" : "Branch added");
  return (
    <div className="space-y-4" data-testid="branches-tab">
      <Card className="flex items-start gap-3"><MapPin className="mt-0.5 h-5 w-5 text-[#0D5C3A]" /><div><p className="font-semibold text-stone-800">Head office</p><p className="text-sm text-stone-500">{business?.address}</p></div></Card>
      {data.branches.map((b) => (
        <Card key={b.id} className="flex items-start justify-between gap-3" data-testid={`branch-${b.id}`}>
          <div className="flex gap-3"><MapPin className="mt-0.5 h-5 w-5 text-[#C85A32]" /><div><p className="font-semibold text-stone-800">{b.name}</p><p className="text-sm text-stone-500">{b.address}</p></div></div>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={() => { setEdit(b.id); setF({ name: b.name, address: b.address }); }} data-testid={`branch-edit-${b.id}`}><Pencil className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => window.confirm(`Remove branch "${b.name}"? Employees there move to Head office.`) && run(() => api.delete(`/org/branches/${b.id}`), "Branch removed")} data-testid={`branch-delete-${b.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        </Card>
      ))}
      <Card className="grid gap-3 sm:grid-cols-[1fr_2fr_auto]">
        <Input placeholder="Branch name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="branch-name-input" />
        <Input placeholder="Branch address" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="branch-address-input" />
        <Button onClick={submit} disabled={!f.name.trim() || !f.address.trim()} data-testid="branch-save-btn" className="bg-[#0D5C3A] hover:bg-[#084229]">{edit ? <Save className="mr-1 h-4 w-4" /> : <Plus className="mr-1 h-4 w-4" />}{edit ? "Save" : "Add branch"}</Button>
      </Card>
    </div>
  );
}

export function PartnersTab() {
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const load = () => api.get("/partners").then((r) => setP(r.data));
  useEffect(() => { load(); }, []);
  if (!p) return null;
  return (
    <div className="space-y-4" data-testid="org-partners-tab">
      <Card className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-stone-600"><b>{p.partners.length}</b> connected business{p.partners.length === 1 ? "" : "es"} · <b>{p.incoming.length}</b> requests waiting · <b>{p.outgoing.length}</b> sent. Partners are external businesses, never employees.</p>
        <Button variant="outline" onClick={() => navigate("/app/partners")} data-testid="org-goto-partners-btn">Open Partners <ArrowRight className="ml-1 h-4 w-4" /></Button>
      </Card>
      <PartnerRequests incoming={p.incoming} outgoing={p.outgoing} onChanged={load} canAct />
    </div>
  );
}

const ACTION_LABELS = {
  employee_added: "Added employee", employee_updated: "Updated employee", employee_suspended: "Suspended employee",
  employee_reactivated: "Reactivated employee", employee_removed: "Removed employee", branch_added: "Added branch", branch_removed: "Removed branch",
  partner_request_sent: "Sent connect request to", partner_request_accepted: "Accepted connect request from", partner_request_declined: "Declined connect request from",
  partner_request_cancelled: "Cancelled connect request", invoice_uploaded: "Uploaded invoice for order", credit_note_attached: "Attached credit note for order", credit_note_replaced: "Replaced credit note for order", invoice_replaced: "Replaced invoice for order", payment_reminder_sent: "Sent payment reminder for order", emergency_order_created: "Created emergency order",
  production_plan_sent: "Sent production plan to", product_stock_set: "Updated stock of", product_stock_deducted: "Stock auto-reduced for delivered order", order_schedule_updated: "Updated order schedule:", payment_paid: "Marked as paid: order", payment_pending: "Marked payment pending: order", partner_removed: "Removed partner", business_login: "Signed in", registration_submitted: "Registered business",
  department_added: "Added department", department_removed: "Removed department", role_added: "Added role",
  order_created: "Placed order", order_accepted: "Accepted order", order_rejected: "Rejected order", order_modified: "Modified order",
  order_modification_accepted: "Accepted changes to order", order_modification_rejected: "Rejected changes to order",
  category_added: "Added category", category_updated: "Updated category", product_moved: "Moved product", category_deleted: "Deleted category",
  product_added: "Added product", product_updated: "Updated product", product_deleted: "Deleted product",
  product_in_stock: "Marked in stock:", product_out_of_stock: "Marked out of stock:",
  org_setup_completed: "Completed organisation setup", org_setup_skipped: "Postponed organisation setup", org_setup_departments_updated: "Updated departments",
};

const GROUPS = [["all", "All activity"], ["orders", "Orders"], ["catalog", "Products & categories"], ["inventory", "Inventory"], ["team", "Team & organisation"], ["partners", "Partners"], ["sign_in", "Sign-ins"]];
const ALL = "all";

function Filter({ value, onChange, items, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="w-full sm:w-52"><SelectValue /></SelectTrigger>
      <SelectContent>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function describe(l) {
  const m = l.meta || {};
  if (l.action === "order_status_change") return `Marked order ${l.target} as ${m.status}`;
  if (l.action === "inventory_adjusted") return `Adjusted stock of ${l.target} (${m.from} → ${m.to})`;
  if (l.action === "inventory_threshold_set") return `Set reorder level of ${l.target} to ${m.threshold}`;
  if (l.action === "product_updated" && m.old_price != null && m.price != null && m.old_price !== m.price) return `Updated product ${l.target} (₹${m.old_price} → ₹${m.price})`;
  const label = ACTION_LABELS[l.action] || l.action.replace(/_/g, " ");
  return l.target && l.action !== "business_login" ? `${label} ${l.target}` : label;
}

export function ActivityTab({ data }) {
  const [f, setF] = useState({ person: ALL, dept: ALL, group: ALL, start: "", end: "" });
  const [logs, setLogs] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => {
    const params = {};
    if (f.person !== ALL) params.actor_id = f.person;
    if (f.dept !== ALL) params.department = f.dept;
    if (f.group !== ALL) params.group = f.group;
    if (f.start) params.start = f.start;
    if (f.end) params.end = f.end;
    api.get("/org/activity", { params }).then((r) => setLogs(r.data.logs)).catch(() => setLogs([]));
  }, [f]);
  const people = [[ALL, "Everyone"], ...(data.owner ? [[data.owner.id, `${data.owner.name} (Super Admin)`]] : []), ...data.employees.map((e) => [e.id, e.name])];
  const depts = [[ALL, "All departments"], ...data.catalog.map((d) => [d.key, d.name])];
  const roleOf = (id) => (id === data.owner?.id ? "Super Admin" : data.employees.find((e) => e.id === id)?.job_role);
  const reset = () => setF({ person: ALL, dept: ALL, group: ALL, start: "", end: "" });
  return (
    <div className="space-y-4" data-testid="activity-tab">
      <Card className="flex flex-wrap items-end gap-3" data-testid="activity-filters">
        <Filter value={f.person} onChange={(v) => set("person", v)} items={people} testid="activity-filter-person" />
        <Filter value={f.dept} onChange={(v) => set("dept", v)} items={depts} testid="activity-filter-department" />
        <Filter value={f.group} onChange={(v) => set("group", v)} items={GROUPS} testid="activity-filter-type" />
        <div><p className="mb-1 text-xs text-stone-400">From</p><Input type="date" value={f.start} onChange={(e) => set("start", e.target.value)} className="w-40" data-testid="activity-filter-start" /></div>
        <div><p className="mb-1 text-xs text-stone-400">To</p><Input type="date" value={f.end} onChange={(e) => set("end", e.target.value)} className="w-40" data-testid="activity-filter-end" /></div>
        <Button variant="ghost" onClick={reset} data-testid="activity-filter-reset">Clear filters</Button>
      </Card>
      <Card>
        {!logs ? <p className="text-sm text-stone-400">Loading…</p> : logs.length === 0 ? <p className="text-sm text-stone-500" data-testid="activity-empty">No activity matches these filters.</p> : (
          <div className="divide-y divide-[#F0ECE1]">
            <p className="pb-2 text-xs text-stone-400" data-testid="activity-count">{logs.length} action{logs.length === 1 ? "" : "s"}{logs.length === 300 ? " (latest 300)" : ""}</p>
            {logs.map((l) => (
              <div key={l.id} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm" data-testid={`activity-${l.id}`}>
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#F0ECE1] text-xs font-bold text-[#0D5C3A]">{(l.actor || "?").slice(0, 1).toUpperCase()}</div>
                  <div className="min-w-0">
                    <p className="text-stone-800"><b>{l.actor}</b>{roleOf(l.actor_id) && <span className="ml-2 text-xs text-stone-400">{roleOf(l.actor_id)}</span>}</p>
                    <p className="text-stone-600">{describe(l)}</p>
                  </div>
                </div>
                <span className="text-xs text-stone-400">{new Date(l.created_at).toLocaleString("en-IN")}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
